import _ from 'lodash';
import { apiBaseUrl } from '../apiConsts';
import { getDataWithRetry, mapPagedResult, sendWithoutBody } from '../fetchUtils';
import { isValidGroupResponse, isValidGroupsResponse } from '../apiValidators';

import type { Group, WithOptionalId, PagedResponse } from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { getDefaultError } from '../../utils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

const mutateGroupAssociation = async (
  groupId: string,
  tableToRef: 'trainers' | 'members' | 'managers',
  action: 'associate' | 'unassociate',
  ids: string[],
): Promise<FetchResult<null>> => {
  if (!ids.length) {
    return createSuccessResult<null>(null);
  }
  return sendWithoutBody(
    `${apiBaseUrl}groups/${groupId}/${action}/${tableToRef}`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(ids),
    },
  );
};

const fetchGroupById = async (
  groupId: string,
): Promise<FetchResult<Group | null>> => {
  const response = await fetchGroupsByIds([groupId]);
  if (!response.success) {
    return response;
  }
  const group = response.data[0] ?? null;
  if (!group) {
    return {
      success: false,
      data: null,
      error: getDefaultError('Group not found'),
      status: response.status,
      durationMs: response.durationMs,
    };
  }
  return {
    success: true,
    data: group,
    status: response.status,
    durationMs: response.durationMs,
  };
};

export const createNewGroup = async (
  group: WithOptionalId<Group>,
  trainerIds: string[],
  memberIds: string[],
): Promise<FetchResult<Group | null>> => {
  const safeTrainerIds = (trainerIds ?? []).filter(Boolean) as string[];
  const safeMemberIds = (memberIds ?? []).filter(Boolean) as string[];
  const createResponse = await getDataWithRetry<Group | null>(
    `${apiBaseUrl}groups`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'POST',
      body: JSON.stringify(group),
    },
    isValidGroupResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }

  const associationCommands: Array<Promise<FetchResult<null>>> = [
    mutateGroupAssociation(
      createResponse.data.id,
      'trainers',
      'associate',
      safeTrainerIds,
    ),
    mutateGroupAssociation(
      createResponse.data.id,
      'members',
      'associate',
      safeMemberIds,
    ),
  ];
  for (const command of associationCommands) {
    const result = await command;
    if (!result.success) {
      return result;
    }
  }

  return fetchGroupById(createResponse.data.id);
};

export const updateGroupData = async (
  oldGroupData: Group,
  newGroupData: Group,
): Promise<FetchResult<Group | null>> => {
  const groupId = newGroupData.id;
  if (!groupId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Group id is required for update'),
    };
  }

  const updateResponse = await getDataWithRetry<Group | null>(
    `${apiBaseUrl}groups`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(newGroupData),
    },
    isValidGroupResponse,
  );
  if (!updateResponse.success) {
    return updateResponse;
  }

  const safeOldTrainerIds = oldGroupData.trainerIds ?? [];
  const safeNewTrainerIds = newGroupData.trainerIds ?? [];
  const safeOldMemberIds = oldGroupData.memberIds ?? [];
  const safeNewMemberIds = newGroupData.memberIds ?? [];
  const safeOldManagerIds = oldGroupData.managerIds ?? [];
  const safeNewManagerIds = newGroupData.managerIds ?? [];

  const trainerIdsToUnassociate = _.difference(
    safeOldTrainerIds,
    safeNewTrainerIds,
  );
  const trainerIdsToAssociate = _.difference(
    safeNewTrainerIds,
    safeOldTrainerIds,
  );
  const memberIdsToUnassociate = _.difference(
    safeOldMemberIds,
    safeNewMemberIds,
  );
  const memberIdsToAssociate = _.difference(safeNewMemberIds, safeOldMemberIds);
  const managerIdsToUnassociate = _.difference(
    safeOldManagerIds,
    safeNewManagerIds,
  );
  const managerIdsToAssociate = _.difference(
    safeNewManagerIds,
    safeOldManagerIds,
  );

  const associationWorkflows: Array<Promise<FetchResult<null>>> = [
    mutateGroupAssociation(
      groupId,
      'trainers',
      'unassociate',
      trainerIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'trainers',
      'associate',
      trainerIdsToAssociate,
    ),
    mutateGroupAssociation(
      groupId,
      'members',
      'unassociate',
      memberIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'members',
      'associate',
      memberIdsToAssociate,
    ),
    mutateGroupAssociation(
      groupId,
      'managers',
      'unassociate',
      managerIdsToUnassociate,
    ),
    mutateGroupAssociation(
      groupId,
      'managers',
      'associate',
      managerIdsToAssociate,
    ),
  ];

  for (const workflow of associationWorkflows) {
    const result = await workflow;
    if (!result.success) {
      return result;
    }
  }

  return fetchGroupById(groupId);
};

export const deleteGroupById = async (
  groupId: string,
): Promise<FetchResult<null>> => {
  if (!groupId) {
    return {
      data: null,
      success: false,
      error: getDefaultError('Group not found'),
    };
  }
  const deleteResult = await sendWithoutBody(`${apiBaseUrl}groups/${groupId}`, {
    method: 'DELETE',
  });
  return deleteResult;
};

export const fetchGroupsByIds = async (
  groupIds: string[],
): Promise<FetchResult<Group[]>> => {
  if (groupIds.length === 0) {
    return createSuccessResult<Group[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Group>>(
    `${apiBaseUrl}groups/getByIds?page=0&size=1000&sort=name`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(groupIds),
    },
    isValidGroupsResponse,
  );
  return mapPagedResult(response);
};
